import { NgTemplateOutlet } from '@angular/common';
import { Component, Input } from '@angular/core';
import type { AnalyticsSection } from '../school-analytics.component';

/**
 * Grey placeholder shaped like a dashboard tab, shown while its figures load
 * (both while the analytics code downloads and while the reports are fetched).
 */
@Component({
  selector: 'app-analytics-skeleton',
  standalone: true,
  template: `
    <div class="animate-pulse" role="status" aria-live="polite">
      <p class="mb-4 text-sm text-blueGray-400">
        <i class="fas fa-spinner fa-spin mr-2"></i>Loading figures…
      </p>
      @if (section === 'overview') {
      <div class="grid gap-4 mb-4" style="grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr))">
        @for (i of tiles(7); track i) {
        <div class="bg-white rounded shadow-lg px-4 py-3 h-24 flex flex-col justify-between">
          <div class="h-3 w-2/3 bg-slate-200 rounded"></div>
          <div class="h-8 w-1/2 bg-slate-200 rounded ml-auto"></div>
        </div>
        }
      </div>
      <ng-container *ngTemplateOutlet="chartCard"></ng-container>
      <div class="grid gap-6 lg:grid-cols-2 mt-6">
        @for (i of tiles(2); track i) {
        <ng-container *ngTemplateOutlet="chartCard"></ng-container>
        }
      </div>
      } @else if (section === 'attendance') {
      <div class="grid gap-6 lg:grid-cols-2">
        @for (i of tiles(2); track i) {
        <ng-container *ngTemplateOutlet="chartCard"></ng-container>
        }
      </div>
      <div class="mt-6"><ng-container *ngTemplateOutlet="chartCard"></ng-container></div>
      <div class="bg-white rounded shadow-lg p-5 mt-6">
        <div class="h-4 w-48 bg-slate-200 rounded mb-4"></div>
        @for (i of tiles(6); track i) {
        <div class="h-6 bg-slate-100 rounded mb-2"></div>
        }
      </div>
      } @else {
      <div class="grid gap-6 lg:grid-cols-2">
        @for (i of tiles(2); track i) {
        <ng-container *ngTemplateOutlet="chartCard"></ng-container>
        }
      </div>
      }
    </div>

    <ng-template #chartCard>
      <div class="bg-white rounded shadow-lg p-5">
        <div class="h-4 w-48 bg-slate-200 rounded mb-2"></div>
        <div class="h-3 w-72 max-w-full bg-slate-100 rounded mb-6"></div>
        <div class="flex items-end gap-3 h-40">
          @for (h of bars; track $index) {
          <div class="flex-1 bg-slate-200 rounded-t" [style.height.%]="h"></div>
          }
        </div>
      </div>
    </ng-template>
  `,
  imports: [NgTemplateOutlet],
})
export class AnalyticsSkeletonComponent {
  @Input() section: AnalyticsSection = 'overview';

  readonly bars = [55, 80, 40, 70, 95, 60, 45, 85, 65, 50];

  tiles(n: number): number[] {
    return Array.from({ length: n }, (_, i) => i);
  }
}
